import { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { v4 as uuidv4 } from 'uuid';

const client = generateClient();

/**
 * Hook for managing lecture CRUD operations with GraphQL API
 */
export const useLectureAPI = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch an existing lecture by ID
   */
  const fetchLecture = useCallback(async (lectureId) => {
    if (!lectureId) return null;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          query GetLecture($id: ID!) {
            getLecture(id: $id) {
              id
              type
              event {
                id
                name {
                  language
                  text
                }
                status
                date
                lectureDocumentsEndDate
              }
              company {
                id
                name
                industry
                status
              }
              speakers {
                items {
                  employeeId
                  employee {
                    id
                    user {
                      firstName
                      lastName
                      middleName
                      title
                    }
                    image {
                      fileName
                      alt
                    }
                  }
                }
              }
              mainSpeaker {
                id
                user {
                  firstName
                  lastName
                  middleName
                  title
                }
                image {
                  fileName
                  alt
                }
              }
              title {
                language
                text
              }
              subjects {
                id
                name {
                  language
                  text
                }
              }
              topics {
                items {
                  topicId
                  topic {
                    id
                    name {
                      language
                      text
                    }
                  }
                }
              }
              content {
                language
                text
              }
              conditions {
                name
                accepted
              }
              presentationLanguage
              image {
                fileName
                alt
              }
              pdfDe {
                url
                name
                s3Path
                fileName
              }
              pdfEn {
                url
                name
                s3Path
                fileName
              }
              presentation {
                url
                name
                s3Path
                fileName
              }
              createdAt
              updatedAt
              companyLecturesId
              eventLecturesId
              industryLecturesId
              lectureMainSpeakerId
            }
          }
        `,
        variables: { id: lectureId }
      });
      
      return response.data.getLecture;
    } catch (error) {
      console.error('Error fetching lecture:', error);
      setError(error.message || 'Failed to fetch lecture');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Create a new lecture
   */
  const createLecture = useCallback(async (lectureData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Create the main lecture record
      const lectureResponse = await client.graphql({
        query: /* GraphQL */ `
          mutation CreateLecture(
            $input: CreateLectureInput!
            $condition: ModelLectureConditionInput
          ) {
            createLecture(input: $input, condition: $condition) {
              id
              type
              presentationLanguage
              title {
                language
                text
              }
              subjects {
                id
                name {
                  language
                  text
                }
              }
              content {
                language
                text
              }
              conditions {
                name
                accepted
              }
              image {
                fileName
                alt
              }
              pdfDe {
                fileName
                name
                s3Path
              }
              pdfEn {
                fileName
                name
                s3Path
              }
              presentation {
                fileName
                name
                s3Path
              }
              companyLecturesId
              eventLecturesId
              industryLecturesId
              lectureMainSpeakerId
            }
          }
        `,
        variables: {
          input: {
            id: uuidv4(),
            type: lectureData.type,
            presentationLanguage: lectureData.presentationLanguage,
            title: lectureData.title,
            subjects: lectureData.subjects,
            content: lectureData.content,
            conditions: lectureData.conditions,
            image: lectureData.image,
            pdfDe: lectureData.pdfDe,
            pdfEn: lectureData.pdfEn,
            presentation: lectureData.presentation,
            companyLecturesId: lectureData.companyId,
            eventLecturesId: lectureData.eventId,
            industryLecturesId: lectureData.industryLecturesId,
            lectureMainSpeakerId: lectureData.mainSpeakerId
          }
        }
      });

      const lecture = lectureResponse.data.createLecture;

      // Create speaker relations
      if (lectureData.speakers && lectureData.speakers.length > 0) {
        await Promise.all(
          lectureData.speakers.map(speakerId =>
            createLectureSpeakerRelation(lecture.id, speakerId)
          )
        );
      }

      // Create topic relations
      if (lectureData.topics && lectureData.topics.length > 0) {
        await Promise.all(
          lectureData.topics.map(topicId =>
            createLectureTopicRelation(lecture.id, topicId)
          )
        );
      }

      return lecture;
    } catch (error) {
      console.error('Error creating lecture:', error);
      setError(error.message || 'Failed to create lecture');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update an existing lecture
   */
  const updateLecture = useCallback(async (lectureId, lectureData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Update the main lecture record
      const lectureResponse = await client.graphql({
        query: /* GraphQL */ `
          mutation UpdateLecture(
            $input: UpdateLectureInput!
            $condition: ModelLectureConditionInput
          ) {
            updateLecture(input: $input, condition: $condition) {
              id
              type
              presentationLanguage
              title {
                language
                text
              }
              subjects {
                id
                name {
                  language
                  text
                }
              }
              content {
                language
                text
              }
              conditions {
                name
                accepted
              }
              image {
                fileName
                alt
              }
              pdfDe {
                fileName
                name
                s3Path
              }
              pdfEn {
                fileName
                name
                s3Path
              }
              presentation {
                fileName
                name
                s3Path
              }
              companyLecturesId
              eventLecturesId
              industryLecturesId
              lectureMainSpeakerId
            }
          }
        `,
        variables: {
          input: {
            id: lectureId,
            type: lectureData.type,
            presentationLanguage: lectureData.presentationLanguage,
            title: lectureData.title,
            subjects: lectureData.subjects,
            content: lectureData.content,
            conditions: lectureData.conditions,
            image: lectureData.image,
            pdfDe: lectureData.pdfDe,
            pdfEn: lectureData.pdfEn,
            presentation: lectureData.presentation,
            lectureMainSpeakerId: lectureData.mainSpeakerId
          }
        }
      });

      const lecture = lectureResponse.data.updateLecture;

      // Update speaker relations
      await updateLectureSpeakers(lectureId, lectureData.speakers || []);

      // Update topic relations
      await updateLectureTopics(lectureId, lectureData.topics || []);

      return lecture;
    } catch (error) {
      console.error('Error updating lecture:', error);
      setError(error.message || 'Failed to update lecture');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Delete a lecture
   */
  const deleteLecture = useCallback(async (lectureId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // First delete all speaker and topic relations
      await deleteAllLectureSpeakers(lectureId);
      await deleteAllLectureTopics(lectureId);

      // Then delete the lecture itself
      const response = await client.graphql({
        query: /* GraphQL */ `
          mutation DeleteLecture(
            $input: DeleteLectureInput!
            $condition: ModelLectureConditionInput
          ) {
            deleteLecture(input: $input, condition: $condition) {
              id
            }
          }
        `,
        variables: {
          input: { id: lectureId }
        }
      });

      return response.data.deleteLecture;
    } catch (error) {
      console.error('Error deleting lecture:', error);
      setError(error.message || 'Failed to delete lecture');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Helper functions for managing many-to-many relations

  const createLectureSpeakerRelation = async (lectureId, employeeId) => {
    try {
      await client.graphql({
        query: /* GraphQL */ `
          mutation CreateLecturesSpeakers(
            $input: CreateLecturesSpeakersInput!
            $condition: ModelLecturesSpeakersConditionInput
          ) {
            createLecturesSpeakers(input: $input, condition: $condition) {
              id
              lectureId
              employeeId
            }
          }
        `,
        variables: {
          input: {
            lectureId,
            employeeId
          }
        }
      });
    } catch (error) {
      console.error('Error creating speaker relation:', error);
    }
  };

  const createLectureTopicRelation = async (lectureId, topicId) => {
    try {
      await client.graphql({
        query: /* GraphQL */ `
          mutation CreateLecturesTopics(
            $input: CreateLecturesTopicsInput!
            $condition: ModelLecturesTopicsConditionInput
          ) {
            createLecturesTopics(input: $input, condition: $condition) {
              id
              lectureId
              topicId
            }
          }
        `,
        variables: {
          input: {
            lectureId,
            topicId
          }
        }
      });
    } catch (error) {
      console.error('Error creating topic relation:', error);
    }
  };

  const updateLectureSpeakers = async (lectureId, newSpeakerIds) => {
    // Fetch existing speakers
    const existingResponse = await client.graphql({
      query: /* GraphQL */ `
        query ListLecturesSpeakers(
          $filter: ModelLecturesSpeakersFilterInput
          $limit: Int
        ) {
          listLecturesSpeakers(filter: $filter, limit: $limit) {
            items {
              id
              employeeId
            }
          }
        }
      `,
      variables: {
        filter: { lectureId: { eq: lectureId } },
        limit: 10000
      }
    });

    const existingSpeakers = existingResponse.data.listLecturesSpeakers.items || [];
    const existingSpeakerIds = existingSpeakers.map(s => s.employeeId);

    // Determine speakers to add and remove
    const speakersToAdd = newSpeakerIds.filter(id => !existingSpeakerIds.includes(id));
    const speakersToRemove = existingSpeakers.filter(s => !newSpeakerIds.includes(s.employeeId));

    // Add new speakers
    await Promise.all(
      speakersToAdd.map(speakerId => createLectureSpeakerRelation(lectureId, speakerId))
    );

    // Remove obsolete speakers
    await Promise.all(
      speakersToRemove.map(speaker =>
        client.graphql({
          query: /* GraphQL */ `
            mutation DeleteLecturesSpeakers(
              $input: DeleteLecturesSpeakersInput!
              $condition: ModelLecturesSpeakersConditionInput
            ) {
              deleteLecturesSpeakers(input: $input, condition: $condition) {
                id
              }
            }
          `,
          variables: {
            input: { id: speaker.id }
          }
        })
      )
    );
  };

  const updateLectureTopics = async (lectureId, newTopicIds) => {
    // Fetch existing topics
    const existingResponse = await client.graphql({
      query: /* GraphQL */ `
        query ListLecturesTopics(
          $filter: ModelLecturesTopicsFilterInput
          $limit: Int
        ) {
          listLecturesTopics(filter: $filter, limit: $limit) {
            items {
              id
              topicId
            }
          }
        }
      `,
      variables: {
        filter: { lectureId: { eq: lectureId } },
        limit: 10000
      }
    });

    const existingTopics = existingResponse.data.listLecturesTopics.items || [];
    const existingTopicIds = existingTopics.map(t => t.topicId);

    // Determine topics to add and remove
    const topicsToAdd = newTopicIds.filter(id => !existingTopicIds.includes(id));
    const topicsToRemove = existingTopics.filter(t => !newTopicIds.includes(t.topicId));

    // Add new topics
    await Promise.all(
      topicsToAdd.map(topicId => createLectureTopicRelation(lectureId, topicId))
    );

    // Remove obsolete topics
    await Promise.all(
      topicsToRemove.map(topic =>
        client.graphql({
          query: /* GraphQL */ `
            mutation DeleteLecturesTopics(
              $input: DeleteLecturesTopicsInput!
              $condition: ModelLecturesTopicsConditionInput
            ) {
              deleteLecturesTopics(input: $input, condition: $condition) {
                id
              }
            }
          `,
          variables: {
            input: { id: topic.id }
          }
        })
      )
    );
  };

  const deleteAllLectureSpeakers = async (lectureId) => {
    const response = await client.graphql({
      query: /* GraphQL */ `
        query ListLecturesSpeakers(
          $filter: ModelLecturesSpeakersFilterInput
          $limit: Int
        ) {
          listLecturesSpeakers(filter: $filter, limit: $limit) {
            items {
              id
            }
          }
        }
      `,
      variables: {
        filter: { lectureId: { eq: lectureId } },
        limit: 10000
      }
    });

    const speakers = response.data.listLecturesSpeakers.items || [];
    
    await Promise.all(
      speakers.map(speaker =>
        client.graphql({
          query: /* GraphQL */ `
            mutation DeleteLecturesSpeakers(
              $input: DeleteLecturesSpeakersInput!
            ) {
              deleteLecturesSpeakers(input: $input) {
                id
              }
            }
          `,
          variables: {
            input: { id: speaker.id }
          }
        })
      )
    );
  };

  const deleteAllLectureTopics = async (lectureId) => {
    const response = await client.graphql({
      query: /* GraphQL */ `
        query ListLecturesTopics(
          $filter: ModelLecturesTopicsFilterInput
          $limit: Int
        ) {
          listLecturesTopics(filter: $filter, limit: $limit) {
            items {
              id
            }
          }
        }
      `,
      variables: {
        filter: { lectureId: { eq: lectureId } },
        limit: 10000
      }
    });

    const topics = response.data.listLecturesTopics.items || [];
    
    await Promise.all(
      topics.map(topic =>
        client.graphql({
          query: /* GraphQL */ `
            mutation DeleteLecturesTopics(
              $input: DeleteLecturesTopicsInput!
            ) {
              deleteLecturesTopics(input: $input) {
                id
              }
            }
          `,
          variables: {
            input: { id: topic.id }
          }
        })
      )
    );
  };

  return {
    fetchLecture,
    createLecture,
    updateLecture,
    deleteLecture,
    isLoading,
    error
  };
};